import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { regexPattern } from '../constants/regexPattern';
import { isNumber } from 'lodash';

// Name Validation
export function validateName(isMandatory: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const Name_REGEXP = regexPattern.name;
    const minLength = 1;
    const maxLength = 50;

    // Mandatory Field Check
    if (!control.value) {
      //If the field is empty and isMandatory is true, it returns a validation error saying the field is mandatory.
      return isMandatory
        ? { validateName: { valid: false, message: 'Required' } }
        : null;
    }
    // Length Validation Check
    if (control.value.length < minLength || control.value.length > maxLength) {
      return {
        validateName: {
          valid: false,
          message: `Name must be between ${minLength} and ${maxLength} characters`,
        },
      };
    }
    // Pattern Validation Check
    if (Name_REGEXP.test(control.value)) {
      return null;
    } else {
      control.markAsTouched();
      return {
        validateName: {
          valid: false,
          message: 'Please use valid name format',
        },
      };
    }
  };
}

// Email Validation
export function validateEmail(isMandatory: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const restrictedTlds: string[] = [
      'com',
      'net',
      'org',
      'edu',
      'gov',
      'cc',
      'in',
      'co',
      'ru',
      'info',
      'fr',
    ];

    // Mandatory Field Check
    if (!value) {
      return isMandatory
        ? { validateEmail: { valid: false, message: 'Required' } }
        : null;
    }

    // General Email Pattern Validation
    const emailValidationResult = Validators.email(control);
    if (emailValidationResult) {
      return {
        validateEmail: {
          valid: false,
          message: 'Please use valid email format (name@domain.com)',
        },
      };
    }

    // Domain Validation
    const domain = value.split('@')[1]; // Extract the part after "@"
    if (domain) {
      const domainParts = domain.split('.');
      const tld = domainParts[domainParts.length - 1]?.toLowerCase(); // Get the last part (TLD)
      if (!restrictedTlds.includes(tld)) {
        return {
          validateEmail: {
            valid: false,
            message: `Email must end with one of the following domains: ${restrictedTlds.join(
              ', '
            )}`,
          },
        };
      }
    }

    return null; // Valid email
  };
}

// Address Validation
export function validateAddress(
  isMandatory: boolean = false,
  min?: number,
  max?: number
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    let minLength: number = 2;
    let maxLength: number = 100;
    //Dev: If provides min or max, use those; otherwise, use defaults (min 2, max 100)
    if (isNumber(min)) {
      minLength = min;
    }
    if (isNumber(max)) {
      maxLength = max;
    }

    // Mandatory Field Check
    if (!control.value) {
      return isMandatory
        ? {
            validateAddress: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }
    // Length Validation Check
    if (control.value.length < minLength || control.value.length > maxLength) {
      control.markAsTouched();
      return {
        validateAddress: {
          valid: false,
          message: `Address must be between ${minLength} and ${maxLength} characters.`,
        },
      };
    }
    return null;
  };
}

// Zip Code Validation
export function validateZipCode(isMandatory: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const ZIP_CODE_REGEXP = regexPattern.globalZipCode;
    const minLength = 5;
    const maxLength = 10; // 5 digits or 9 with hyphen

    // Mandatory Field Check
    if (!control.value) {
      return isMandatory
        ? {
            validateZipCode: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }
    // Length Validation Check
    // if (control.value.length < minLength || control.value.length > maxLength) {
    //   return {
    //     validateZipCode: {
    //       valid: false,
    //       message: `Error: Zip code must be ${minLength} to ${maxLength} characters long.`
    //     }
    //   };
    // }
    // Pattern Validation Check
    return ZIP_CODE_REGEXP.test(control.value)
      ? null
      : {
          validateZipCode: {
            valid: false,
            message:
              'Please use valid Zip format (12345, 12345-6789, 12345 6789, or 123456789)',
          },
        };
  };
}

// City Validation
export function validateCity(isMandatory: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const City_REGEXP = regexPattern.city;
    const minLength: number = 2;
    const maxLength: number = 50;

    // Mandatory Field Check
    if (!control.value) {
      return isMandatory
        ? {
            validateCity: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }
    // Length Validation Check
    if (control.value.length < minLength || control.value.length > maxLength) {
      return {
        validateCity: {
          valid: false,
          message: `City must be between ${minLength} and ${maxLength} characters.`,
        },
      };
    }
    // Pattern Validation Check
    return City_REGEXP.test(control.value)
      ? null
      : {
          validateCity: {
            valid: false,
            message: 'Please enter valid city name.',
          },
        };
  };
}

// Phone Number Validation
export function validatePhoneNumber(isMandatory: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const PHONE_REGEXP = regexPattern.globalPhoneNumber;

    // Mandatory Field Check
    if (!control.value) {
      return isMandatory
        ? {
            validatePhoneNumber: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }
    // Pattern Validation Check
    return PHONE_REGEXP.test(value)
      ? null
      : {
          validatePhoneNumber: {
            valid: false,
            message: 'Please enter valid 10-digit phone number',
          },
        };
  };
}

// Phone Number Masked TextBox Validation
export function validatePhoneNumberMaskedTextBox(
  isMandatory: boolean = false
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const length: number = 10;
    // Mandatory Field Check
    if (!control.value) {
      return isMandatory
        ? {
            validatePhoneNumberMaskedTextBox: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }
    // Length Validation Check
    if (control.value.trim().length !== length) {
      return {
        validatePhoneNumberMaskedTextBox: {
          valid: false,
          message: 'Please enter valid 10-digit phone number',
        },
      };
    }
    return null;
  };
}

export function markAsTouchedIfInvalidDate(control: AbstractControl): void {
  // To show error if patched or defaulted date is invalid
  if (control?.value && !control.valid) {
    control.markAsTouched();
  }
}

// File Size Validation
export function validateFileSize(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value;

    // Check if there's a file selected
    if (!file) {
      return null; // No file to validate, so no error
    }
    // If file is selected, check the size (file size is in bytes)
    const fileSize = file.size;
    // Validate that the file size is 0KB (0 bytes)
    if (fileSize !== 0) {
      return {
        validateFileSize: {
          valid: false,
          message: 'File size must be 0KB',
        },
      };
    }
    return null; // Validation passed (file size is 0KB)
  };
}

// Validation to allow only Numbers
export function validateNumber(isMandatory: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const NUMBER_REGEXP = regexPattern.globalOnlyNumbers;

    // Mandatory Field Check
    if (!value) {
      return isMandatory
        ? {
            validateNumber: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }

    // Pattern Validation Check for Numbers Only
    if (!NUMBER_REGEXP.test(value)) {
      control.markAsTouched();
      return {
        validateNumber: {
          valid: false,
          message: 'Please enter only numeric values.',
        },
      };
    }
    return null;
  };
}

// AlphaNumeric Validation
export function validateAlphaNumeric(
  isMandatory: boolean = false
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const Name_REGEXP = /^[a-zA-Z0-9]+$/; // Alphanumeric pattern
    const minTwoAlphabetPattern = /[a-zA-Z].*[a-zA-Z]/; // Pattern to check for at least two alphabets
    const minLength = 2;
    const maxLength = 30;

    // Mandatory Field Check
    if (!value) {
      // If the field is empty and isMandatory is true, return error
      return isMandatory
        ? {
            validateAlphaNumeric: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }

    // Length Validation Check
    if (value.length < minLength || value.length > maxLength) {
      return {
        validateAlphaNumeric: {
          valid: false,
          message: `Name must be between ${minLength} and ${maxLength} characters.`,
        },
      };
    }

    // Minimum Alphabet Requirement Check
    if (!minTwoAlphabetPattern.test(value)) {
      control.markAsTouched();
      return {
        validateAlphaNumeric: {
          valid: false,
          message: 'Name must contain at least two alphabetic characters.',
        },
      };
    }

    // Pattern Validation Check for alphanumeric characters only
    if (Name_REGEXP.test(value)) {
      return null;
    } else {
      control.markAsTouched();
      return {
        validateAlphaNumeric: {
          valid: false,
          message: 'Name must contain only alphanumeric characters.',
        },
      };
    }
  };
}

// Validation to allow length of 6, 7 or 9
export function validateNycid(
  nycIdOnly: boolean = false,
  isMandatory: boolean = false
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value ? control.value.toString() : '';

    // Mandatory Field Check
    if (!value) {
      // If the field is empty and isMandatory is true, return error
      return isMandatory
        ? {
            validateNycid: {
              valid: false,
              message: 'Required',
            },
          }
        : null;
    }

    if (!value || value.length === 9) {
      if (nycIdOnly) {
        return null;
      } else if (value.length === 6 || value.length === 7) {
        return null;
      }
    }
    return {
      validateNycid: {
        valid: false,
        message:
          'Student ID should be ' + (nycIdOnly ? '' : '6, 7 or') + '9 digits',
      },
    };
  };
}
